/**
 * Konva 이벤트 ↔ IPointerEvent 번역.
 *
 * - 네임스페이스 문자열('mousedown.ns touchstart.ns')은 그대로 Konva에 전달한다.
 * - Konva 이벤트 객체의 target을 핸들로 바꿔 쓰지 않고(버블링 중 Konva가 evt.target을 읽음) 얇은 래퍼로 감싼다.
 *   target/currentTarget은 WeakMap 핸들 캐시(handleOf)로 구해 동일성을 보장한다.
 * - cancelBubble setter는 원본 이벤트에 반영돼 Konva 버블링을 멈춘다.
 * - 'scaleXChange' 같은 속성 변경 이벤트는 evt가 없고 oldVal/newVal을 가진다(보존 대상).
 */
import type Konva from 'konva'
import type { INode, IPointerEvent, PointerEventHandler } from '../IRenderer'
import { handleOf } from './KonvaNode'

type RawEvent = {
  evt?: Event
  type?: string
  target?: Konva.Node
  currentTarget?: Konva.Node
  cancelBubble?: boolean
  oldVal?: unknown
  newVal?: unknown
}

export class KonvaPointerEvent implements IPointerEvent {
  constructor(private readonly raw: RawEvent) {}

  get evt(): MouseEvent | TouchEvent | WheelEvent | PointerEvent | undefined {
    return this.raw.evt as MouseEvent | TouchEvent | WheelEvent | PointerEvent | undefined
  }

  get type(): string {
    return this.raw.type ?? ''
  }

  get target(): INode {
    const node = this.raw.target ?? this.raw.currentTarget
    if (!node) throw new Error('[KonvaEvents] 이벤트에 target이 없습니다')
    return handleOf(node)
  }

  get currentTarget(): INode {
    const node = this.raw.currentTarget ?? this.raw.target
    if (!node) throw new Error('[KonvaEvents] 이벤트에 currentTarget이 없습니다')
    return handleOf(node)
  }

  get cancelBubble(): boolean {
    return Boolean(this.raw.cancelBubble)
  }

  set cancelBubble(v: boolean) {
    this.raw.cancelBubble = v
  }

  get oldVal(): unknown {
    return this.raw.oldVal
  }

  get newVal(): unknown {
    return this.raw.newVal
  }
}

/** 엔진 핸들러를 Konva 리스너로 감싼다 */
export function toKonvaListener(handler: PointerEventHandler): (e: RawEvent) => void {
  return (e: RawEvent): void => {
    handler(new KonvaPointerEvent(e))
  }
}
